import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Upload, FileText, CheckCircle, AlertCircle, Download, Users, Calendar, 
  CreditCard, Stethoscope, ArrowRight, X, Loader2, HelpCircle 
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Define our target fields for each import type
const TARGET_FIELDS = {
  patients: [
    { key: 'name', label: 'Full Name', required: false, description: 'Client full name' },
    { key: 'firstName', label: 'First Name', required: false, description: 'Client first name' },
    { key: 'lastName', label: 'Last Name', required: false, description: 'Client last name' },
    { key: 'email', label: 'Email', required: true, description: 'Client email address' },
    { key: 'phone', label: 'Phone', required: false, description: 'Phone number' },
    { key: 'creditBalance', label: 'Credit Balance', required: false, description: 'Account credits/balance' },
    { key: 'totalSpent', label: 'Total Spent', required: false, description: 'Lifetime spending' },
    { key: 'lastVisit', label: 'Last Visit', required: false, description: 'Last appointment date' },
    { key: 'membership', label: 'Membership', required: false, description: 'Membership plan name' },
    { key: 'address', label: 'Address', required: false, description: 'Street address' },
    { key: 'city', label: 'City', required: false, description: 'City' },
    { key: 'state', label: 'State', required: false, description: 'State' },
    { key: 'zipCode', label: 'Zip Code', required: false, description: 'Postal code' },
    { key: 'birthdate', label: 'Birthdate', required: false, description: 'Date of birth' },
    { key: 'notes', label: 'Notes', required: false, description: 'Additional notes' },
  ],
  memberships: [
    { key: 'name', label: 'Plan Name', required: true, description: 'Membership plan name' },
    { key: 'price', label: 'Price', required: false, description: 'Monthly price (can be set later)' },
    { key: 'credits', label: 'Credits', required: false, description: 'Credits per month' },
    { key: 'benefits', label: 'Benefits', required: false, description: 'Pipe-separated benefits' },
  ],
  treatments: [
    { key: 'name', label: 'Treatment Name', required: true, description: 'Service name' },
    { key: 'category', label: 'Category', required: false, description: 'Service category' },
    { key: 'price', label: 'Price', required: false, description: 'Service price (can be set later)' },
    { key: 'duration', label: 'Duration', required: false, description: 'Duration in minutes' },
    { key: 'description', label: 'Description', required: false, description: 'Service description' },
  ],
  transactions: [
    { key: 'date', label: 'Date', required: false, description: 'Transaction date' },
    { key: 'clientName', label: 'Client Name', required: false, description: 'Client name' },
    { key: 'clientEmail', label: 'Client Email', required: false, description: 'Client email' },
    { key: 'phone', label: 'Phone', required: false, description: 'Client phone' },
    { key: 'items', label: 'Items/Services', required: false, description: 'Purchased items' },
    { key: 'amount', label: 'Amount', required: false, description: 'Transaction amount' },
    { key: 'discount', label: 'Discount', required: false, description: 'Discount applied' },
    { key: 'promoCode', label: 'Promo Code', required: false, description: 'Promo code used' },
    { key: 'paymentMethod', label: 'Payment Method', required: false, description: 'Payment type' },
    { key: 'type', label: 'Type', required: false, description: 'Transaction type' },
  ]
};

const DataImport = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: Select type & upload, 2: Map columns, 3: Review & Import
  const [importing, setImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importType, setImportType] = useState('patients');
  const [csvData, setCsvData] = useState({ headers: [], rows: [], totalRows: 0 });
  const [columnMapping, setColumnMapping] = useState({});
  const [importResults, setImportResults] = useState(null);

  const importTypeConfig = {
    patients: { label: 'Clients', icon: Users, color: 'text-blue-600', description: 'Import your client list' },
    memberships: { label: 'Memberships', icon: CreditCard, color: 'text-purple-600', description: 'Import membership plans' },
    treatments: { label: 'Treatments', icon: Stethoscope, color: 'text-green-600', description: 'Import services/treatments' },
    transactions: { label: 'Transactions', icon: Calendar, color: 'text-orange-600', description: 'Import transaction history' },
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv') || file.type === 'application/vnd.ms-excel')) {
      setSelectedFile(file);
      parseCSV(file);
    } else {
      toast({
        title: 'Invalid File',
        description: 'Please select a valid CSV file.',
        variant: 'destructive'
      });
    }
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    
    reader.onerror = () => {
      toast({
        title: 'File Read Error',
        description: 'Could not read the file. Please try again.',
        variant: 'destructive'
      });
    };
    
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          toast({
            title: 'Invalid CSV',
            description: 'CSV file must have headers and at least one data row.',
            variant: 'destructive'
          });
          return;
        }
        
        // Parse headers - handle quoted values
        const headerLine = lines[0];
        const headers = parseCSVLine(headerLine);
        
        if (headers.length === 0) {
          toast({
            title: 'No Columns Found',
            description: 'Could not parse column headers from your CSV.',
            variant: 'destructive'
          });
          return;
        }
        
        // Parse first few rows for preview
        const rows = lines.slice(1, 6).map(line => {
          const values = parseCSVLine(line);
          return headers.reduce((obj, header, index) => {
            obj[header] = values[index] || '';
            return obj;
          }, {});
        });
        
        setCsvData({ headers, rows, totalRows: lines.length - 1 });
        
        // Auto-detect column mappings
        autoDetectMappings(headers);
        
        toast({
          title: 'CSV Loaded!',
          description: `Found ${headers.length} columns and ${lines.length - 1} rows.`
        });
        
        setStep(2);
      } catch (error) {
        console.error('CSV parse error:', error);
        toast({
          title: 'Parse Error',
          description: 'Could not parse the CSV file. Please check the format.',
          variant: 'destructive'
        });
      }
    };
    reader.readAsText(file);
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    
    return result;
  };

  const autoDetectMappings = (headers) => {
    const mapping = {};
    const targetFields = TARGET_FIELDS[importType];
    
    // Common column name variations - expanded for RepeatMD and other platforms
    const variations = {
      name: ['name', 'full name', 'fullname', 'client name', 'customer name', 'patient name', 'client', 'customer', 'member name', 'contact name'],
      firstName: ['first name', 'firstname', 'first', 'fname', 'given name', 'forename'],
      lastName: ['last name', 'lastname', 'last', 'lname', 'surname', 'family name'],
      email: ['email', 'e-mail', 'email address', 'emailaddress', 'client email', 'customer email', 'contact email', 'member email', 'primary email', 'mail', 'e mail', 'email_address', 'client_email', 'user email', 'account email'],
      phone: ['phone', 'telephone', 'tel', 'mobile', 'cell', 'phone number', 'phonenumber', 'contact', 'phone_number', 'cell phone', 'mobile phone', 'primary phone', 'contact number'],
      creditBalance: ['credit', 'credits', 'balance', 'credit balance', 'account balance', 'repeatcash', 'reward points', 'points', 'account credits', 'wallet', 'store credit'],
      totalSpent: ['total', 'total spent', 'lifetime value', 'ltv', 'spending', 'revenue', 'list price', 'amount', 'membership ltv', 'total revenue', 'lifetime spend', 'total purchases', 'grand total'],
      lastVisit: ['last visit', 'lastvisit', 'last appointment', 'last seen', 'visit date', 'last service', 'last booking', 'recent visit'],
      membership: ['membership', 'plan', 'subscription', 'member type', 'membership plan', 'type', 'membership type', 'plan name', 'membership name', 'program'],
      address: ['address', 'street', 'street address', 'address1', 'address 1', 'address line 1', 'mailing address'],
      city: ['city', 'town', 'locality'],
      state: ['state', 'province', 'region', 'st'],
      zipCode: ['zip', 'zipcode', 'zip code', 'postal', 'postal code', 'postcode', 'zip/postal'],
      birthdate: ['birthdate', 'birthday', 'dob', 'date of birth', 'birth date', 'birth_date', 'date_of_birth', 'bday', 'b-day', 'born', 'birth'],
      notes: ['notes', 'comments', 'memo', 'description', 'note', 'comment', 'remarks'],
      items: ['items', 'services', 'products', 'item', 'service', 'product', 'treatment', 'treatments'],
      amount: ['amount', 'total', 'price', 'list price', 'subtotal', 'grand total', 'sale amount', 'transaction amount', 'membership ltv'],
      discount: ['discount', 'discounts', 'savings', 'discount amount'],
      promoCode: ['promo', 'promo code', 'promocode', 'coupon', 'coupon code', 'discount code', 'voucher'],
      paymentMethod: ['payment', 'payment method', 'payment type', 'method', 'pay method', 'payment_method', 'pay type'],
      date: ['date', 'transaction date', 'order date', 'purchase date', 'created', 'created date', 'sale date', 'first bill date'],
      clientName: ['name', 'client', 'customer', 'client name', 'customer name'],
      clientEmail: ['email', 'client email', 'customer email'],
      category: ['category', 'type', 'service type', 'treatment type', 'service category'],
      duration: ['duration', 'time', 'minutes', 'length', 'service time'],
      price: ['price', 'cost', 'rate', 'amount', 'unit price', 'list price', 'membership price', 'monthly price', 'plan price'],
      benefits: ['benefits', 'perks', 'features', 'includes', 'inclusions'],
    };
    
    headers.forEach(header => {
      const headerLower = header.toLowerCase().trim();
      
      targetFields.forEach(field => {
        // Check if already mapped
        if (mapping[field.key]) return;
        
        // Check exact match
        if (headerLower === field.key.toLowerCase()) {
          mapping[field.key] = header;
          return;
        }
        
        // Check variations
        const fieldVariations = variations[field.key] || [];
        if (fieldVariations.some(v => headerLower === v || headerLower.includes(v))) {
          mapping[field.key] = header;
        }
      });
    });
    
    setColumnMapping(mapping);
  };

  const handleMappingChange = (fieldKey, csvColumn) => {
    setColumnMapping(prev => ({
      ...prev,
      [fieldKey]: csvColumn === 'none' ? null : csvColumn
    }));
  };

  const handleImport = async () => {
    setImporting(true);
    setImportResults(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('practiceId', user?.practiceId || localStorage.getItem('practiceId'));
      formData.append('columnMapping', JSON.stringify(columnMapping));

      const response = await axios.post(`${API}/import/${importType}-mapped`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResults(response.data);
      setStep(3);
      
      toast({
        title: response.data.success > 0 ? 'Import Complete!' : 'Import Issues',
        description: `Imported ${response.data.success} records. ${response.data.skipped} skipped, ${response.data.errors} errors.`,
        variant: response.data.success > 0 ? 'default' : 'destructive'
      });
      
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: 'Import Failed',
        description: error.response?.data?.detail || 'Failed to import data.',
        variant: 'destructive'
      });
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setStep(1);
    setSelectedFile(null);
    setCsvData({ headers: [], rows: [], totalRows: 0 });
    setColumnMapping({});
    setImportResults(null);
  };

  const targetFields = TARGET_FIELDS[importType] || [];
  const requiredFieldsMapped = targetFields
    .filter(f => f.required)
    .every(f => columnMapping[f.key]);

  return (
    <div className="space-y-6" data-testid="data-import-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Data Import</h1>
        <p className="text-gray-600">Import your data from any platform - we'll help you map the columns</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4 py-4">
        {[
          { num: 1, label: 'Upload CSV' },
          { num: 2, label: 'Map Columns' },
          { num: 3, label: 'Results' }
        ].map((s, idx) => (
          <React.Fragment key={s.num}>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
              step === s.num ? 'bg-primary text-white' : 
              step > s.num ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
            }`}>
              {step > s.num ? <CheckCircle className="h-4 w-4" /> : <span>{s.num}</span>}
              <span className="text-sm font-medium">{s.label}</span>
            </div>
            {idx < 2 && <ArrowRight className="h-4 w-4 text-gray-300" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Select Type & Upload */}
      {step === 1 && (
        <div className="space-y-6">
          {/* Import Type Selection */}
          <Card>
            <CardHeader>
              <CardTitle>What are you importing?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(importTypeConfig).map(([type, config]) => {
                  const Icon = config.icon;
                  return (
                    <div
                      key={type}
                      className={`cursor-pointer p-4 rounded-lg border-2 transition-all hover:shadow-md ${
                        importType === type ? 'border-primary bg-primary/5' : 'border-gray-200'
                      }`}
                      onClick={() => setImportType(type)}
                      data-testid={`import-type-${type}`}
                    >
                      <Icon className={`h-8 w-8 mb-2 ${config.color}`} />
                      <p className="font-semibold">{config.label}</p>
                      <p className="text-xs text-gray-500">{config.description}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* File Upload */}
          <Card>
            <CardHeader>
              <CardTitle>Upload Your CSV</CardTitle>
              <CardDescription>
                Upload a CSV file from any platform - RepeatMD, Boulevard, Zenoti, Excel, etc.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary transition-colors">
                <input
                  type="file"
                  id="csv-upload"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-700">Click to upload or drag and drop</p>
                  <p className="text-sm text-gray-500 mt-1">CSV files only (MAX. 10MB)</p>
                </label>
              </div>
              
              {selectedFile && (
                <div className="mt-4 flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                  <FileText className="h-5 w-5 text-green-600" />
                  <span className="text-green-700">{selectedFile.name}</span>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedFile(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 2: Map Columns */}
      {step === 2 && (
        <div className="space-y-6">
          {/* Actions at TOP */}
          <div className="flex justify-between items-center bg-white p-4 rounded-lg border shadow-sm sticky top-0 z-10">
            <Button variant="outline" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={!requiredFieldsMapped || importing}
              data-testid="import-btn"
              size="lg"
              className="px-8"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Importing...
                </>
              ) : (
                `Import ${csvData.totalRows} Records`
              )}
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Map Your Columns</CardTitle>
              <CardDescription>
                We detected {csvData.headers.length} columns in your file. Match them to our fields below.
                <span className="text-primary font-medium"> * Required fields</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Auto-detected info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <HelpCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800 font-medium">We've auto-detected some mappings!</p>
                    <p className="text-sm text-blue-700">Review and adjust as needed. Only mapped columns will be imported.</p>
                  </div>
                </div>
              </div>

              {/* Column Mapping Grid */}
              <div className="grid gap-4">
                {targetFields.map(field => (
                  <div key={field.key} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <Label className="font-medium">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      <p className="text-xs text-gray-500">{field.description}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                    <div className="w-64">
                      <Select
                        value={columnMapping[field.key] || 'none'}
                        onValueChange={(value) => handleMappingChange(field.key, value)}
                      >
                        <SelectTrigger className={columnMapping[field.key] ? 'border-green-500' : ''}>
                          <SelectValue placeholder="Select column..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- Don't import --</SelectItem>
                          {csvData.headers.map(header => (
                            <SelectItem key={header} value={header}>{header}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>

              {/* Preview */}
              {csvData.rows.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Preview (first {csvData.rows.length} rows)</h4>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          {csvData.headers.map(h => (
                            <th key={h} className="px-3 py-2 text-left font-medium text-gray-700">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvData.rows.map((row, idx) => (
                          <tr key={idx} className="border-t">
                            {csvData.headers.map(h => (
                              <td key={h} className="px-3 py-2 text-gray-600">{row[h] || '-'}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Total rows to import: <strong>{csvData.totalRows}</strong></p>
                </div>
              )}
            </CardContent>
          </Card>

        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && importResults && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {importResults.success > 0 ? (
                <CheckCircle className="h-6 w-6 text-green-600" />
              ) : (
                <AlertCircle className="h-6 w-6 text-red-600" />
              )}
              Import Complete
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{importResults.success}</p>
                <p className="text-sm text-green-700">Imported</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-yellow-600">{importResults.skipped}</p>
                <p className="text-sm text-yellow-700">Skipped (duplicates)</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-red-600">{importResults.errors}</p>
                <p className="text-sm text-red-700">Errors</p>
              </div>
            </div>

            {importResults.errorDetails && importResults.errorDetails.length > 0 && (
              <div className="bg-red-50 rounded-lg p-4">
                <p className="font-medium text-red-800 mb-2">Error Details:</p>
                <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                  {importResults.errorDetails.map((err, idx) => (
                    <li key={idx}>• {err}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-center gap-4 pt-4">
              <Button variant="outline" onClick={resetImport}>
                Import More Data
              </Button>
              <Button onClick={() => {
                const routes = {
                  patients: '/admin/patients',
                  memberships: '/admin/memberships',
                  treatments: '/admin/treatments',
                  transactions: '/admin/transactions'
                };
                window.location.href = routes[importType] || '/admin';
              }}>
                View Imported Data
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DataImport;
